/**
 * Blog Service (Hybrid Approach)
 * 
 * Service layer for blog-related operations using a hybrid approach:
 * - Static content: Read from markdown files (fast, SEO-friendly)
 * - Dynamic features: API calls for metrics, likes, shares (real-time)
 * 
 * This provides the best of both worlds: fast static content + dynamic interactions.
 */

import { apiGet, apiPost } from './apiClient';
import { apiEndpoints } from '../config/environment';
import { staticBlogService } from './staticBlogService';
import type {
  BlogPost,
  BlogQueryOptions,
  BlogMetrics,
  BlogListResponse,
  BlogPostResponse,
} from '../types/blog.types';
import type { ApiResponse } from '../types/api.types';

/**
 * Blog Service API
 * 
 * Uses API endpoints in production, static markdown files in development
 */
export const blogService = {
  /**
   * Get list of blog posts
   * Uses API in production, static files in development
   */
  getBlogList: async (
    options: BlogQueryOptions = {}
  ): Promise<BlogPost[]> => {
    // In production, use API; in development, use static files
    if (process.env.NODE_ENV === 'production') {
      const endpoint = apiEndpoints.blogs.list;
      const queryParams = new URLSearchParams();
      
      if (options.page) queryParams.append('page', options.page.toString());
      if (options.limit) queryParams.append('limit', options.limit.toString());
      if (options.category) queryParams.append('category', options.category);
      if (options.tag) queryParams.append('tag', options.tag);
      if (options.search) queryParams.append('search', options.search);
      if (options.sortBy) queryParams.append('sortBy', options.sortBy);
      if (options.sortOrder) queryParams.append('sortOrder', options.sortOrder);
      if (options.featuredOnly) queryParams.append('featuredOnly', 'true');
      
      const queryString = queryParams.toString();
      const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;
      
      const response = await apiGet<BlogListResponse>(fullEndpoint);
      return response.data || [];
    }
    
    // Development: use static service
    return await staticBlogService.getBlogList(options);
  },

  /**
   * Get a single blog post by ID
   * Uses API in production, static files in development
   */
  getBlogById: async (id: string): Promise<BlogPost | null> => {
    if (process.env.NODE_ENV === 'production') {
      const endpoint = apiEndpoints.blogs.byId(id);
      const response = await apiGet<BlogPostResponse>(endpoint);
      return response.data || null;
    }
    
    return await staticBlogService.getBlogById(id);
  },

  /**
   * Get a single blog post by slug
   * Uses API in production, static files in development
   */
  getBlogBySlug: async (slug: string): Promise<BlogPost | null> => {
    if (process.env.NODE_ENV === 'production') {
      const endpoint = apiEndpoints.blogs.bySlug(slug);
      const response = await apiGet<BlogPostResponse>(endpoint);
      return response.data || null;
    }
    
    return await staticBlogService.getBlogBySlug(slug);
  },

  /**
   * Like a blog post
   */
  likeBlog: async (id: string): Promise<BlogMetrics> => {
    const endpoint = apiEndpoints.blogs.like(id);
    const response = await apiPost<ApiResponse<BlogMetrics>>(endpoint, {});
    return response.data;
  },

  /**
   * Share a blog post
   */
  shareBlog: async (id: string): Promise<BlogMetrics> => {
    const endpoint = apiEndpoints.blogs.share(id);
    const response = await apiPost<ApiResponse<BlogMetrics>>(endpoint, {});
    return response.data;
  },

  /**
   * Get blog post metrics
   */
  getBlogMetrics: async (id: string): Promise<BlogMetrics> => {
    const endpoint = apiEndpoints.blogs.metrics(id);
    const response = await apiGet<ApiResponse<BlogMetrics>>(endpoint);
    return response.data;
  },
};

